import { Heading, Text } from '@radix-ui/themes';
import clsx from 'clsx';
import CandidateFullName from 'components/Candidates/CandidateFullName';
import IsAxiosError from 'components/common/IsAxiosError';
import MutedText from 'components/common/MutedText';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertCombobox from 'components/HubertUI/HubertCombobox';
import HubertDialog from 'components/HubertUI/HubertDialog';
import type { ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useJobInterviewActionsMutation } from 'rtk/query/apis/jobs';
import type { TJobInterviewActionName } from 'rtk/query/apis/jobs/jobs.api.types';
import { toast } from 'sonner';
import type { TSelectBase } from 'types/general.types';
import type { TInterviewWithRelations } from 'types/interview.types';

type TModifiedTSelectBase = TSelectBase & {
  value: TJobInterviewActionName;
};

interface Props extends ComponentProps<'div'> {
  jobId: string | undefined;
  selectedInterviews: TInterviewWithRelations[];
}

const SelectedInterviewActionsDialog = ({ jobId, selectedInterviews = [] }: Props) => {
  const { t } = useTranslation('PaginatedManagerTable');
  const [value, setValue] = useState<TModifiedTSelectBase>();

  const [
    jobInterviewActionsRequest,
    {
      isLoading: isJobInterviewActionsRequestLoading,
      isError: isJobInterviewActionsRequestError,
      error: jobInterviewActionsRequestError,
    },
  ] = useJobInterviewActionsMutation();

  const actionList = useMemo<TModifiedTSelectBase[]>(
    () => [
      {
        value: 'reset',
        label: t('interviewActions.actions.reset.label'),
        variant: 'destructive-outline',
        description: t('interviewActions.actions.reset.description'),
        color: 'warning',
        onClick: (value) => {
          console.log('selected item value', value);
          console.log('selectedInterviews', selectedInterviews);
        },
      },
      {
        value: 'delete',
        label: t('interviewActions.actions.delete.label'),
        description: t('interviewActions.actions.delete.description'),
        variant: 'destructive',
        color: 'danger',
        onClick: (value) => {
          console.log('selected item value', value);
          console.log('selectedInterviews', selectedInterviews);
        },
      },
      {
        value: 'apply-current-job-template-process-id',
        label: t('interviewActions.actions.applyCurrentProcessId.label'),
        variant: 'info',
        description: t('interviewActions.actions.applyCurrentProcessId.description'),
        color: 'info',
        onClick: (value) => {
          console.log('selected item value', value);
          console.log('selectedInterviews', selectedInterviews);
        },
      },
    ],
    [t],
  );

  return (
    <HubertDialog>
      <HubertDialog.Trigger>
        <HubertButton disabled={selectedInterviews.length === 0} variant={'outline'} size={'sm'}>
          {t('interviewActions.triggerButton')}
        </HubertButton>
      </HubertDialog.Trigger>
      <HubertDialog.Content
        position="center"
        className={clsx('min-h-[50%]', 'flex flex-col justify-between gap-4', 'p-4')}
        portalContent={false}
      >
        <div className="flex flex-col justify-start gap-4">
          <div className="flex flex-row items-baseline justify-start gap-2">
            <Heading size={'6'}>{t('interviewActions.heading')}</Heading>
            <MutedText>{t('interviewActions.subtitle', { count: selectedInterviews.length })}</MutedText>
          </div>

          <div className="hubert-vertical-center flex flex-row justify-start gap-4">
            <Text>{t('interviewActions.chooseAction')}</Text>
            <HubertCombobox
              itemName="item"
              items={actionList}
              disabled={selectedInterviews.length === 0}
              onValueChange={(value) => {
                console.log('onValueChange', value);
                const index = actionList.findIndex((x) => x.value === value);
                if (index > -1) {
                  setValue(actionList[index]);
                } else {
                  setValue(undefined);
                }
              }}
            />
          </div>
          <div className={clsx('min-h-6')}>{value ? value.description : null}</div>
        </div>

        <div className={clsx('overflow-y-auto', 'max-h-96')}>
          {selectedInterviews && selectedInterviews.length ? (
            selectedInterviews.map((x) => {
              return (
                <div
                  key={x.application_id}
                  className={clsx('m-2 p-2', 'flex flex-row justify-between gap-4', 'rounded-md', 'border')}
                >
                  <CandidateFullName candidate={x.candidate} />
                </div>
              );
            })
          ) : (
            <Text className="flex justify-center" as="div">
              {t('interviewActions.noSelection')}
            </Text>
          )}
        </div>

        {isJobInterviewActionsRequestError ? <IsAxiosError error={jobInterviewActionsRequestError} /> : null}

        {value ? (
          <div className={clsx(`bg-${value.color}-50 rounded-md p-4`)}>
            <div
              className={clsx('flex items-center justify-center align-middle', `text-${value.color}-900`, 'min-h-20')}
            >
              {/* TODO(i18n): Category 4 — sentence split across JSX with inline <strong>; needs <Trans> component */}
              <Text as="span">
                Are you sure you want to{' '}
                <strong>
                  {value.label} these {selectedInterviews.length} interviews?
                </strong>
              </Text>
            </div>

            <div className="hubert-vertical-center flex flex-row justify-center gap-4">
              <HubertButton
                variant={value.variant ? value.variant : 'info'}
                onClick={() => {
                  if (!value || !value.value.length) {
                    return;
                  }

                  if (!jobId) {
                    throw new Error('Missing job ID.');
                  }

                  jobInterviewActionsRequest({
                    actionName: value.value,
                    jobId,
                    // @ts-ignore
                    interviewIds: selectedInterviews.map((item) => item.interviewId),
                  })
                    .unwrap()
                    .then((result) => {
                      console.log('result', result);

                      if (Array.isArray(result)) {
                        result.map((x) => {
                          if (x.success) {
                            toast.success(x.message);
                          } else {
                            toast.error(x.message);
                          }
                        });
                      } else {
                        toast.info(result.message);
                      }
                    })
                    .catch((rejected) => {
                      console.log('resetInterviewsRequest rejected', rejected);
                      toast.error(t('interviewActions.toast.genericError', { error: JSON.stringify(rejected) }));
                    });
                  return;
                }}
                disabled={selectedInterviews.length === 0 || !value}
                loading={isJobInterviewActionsRequestLoading}
              >
                {t('interviewActions.confirmButton', { action: value.label })}
              </HubertButton>
            </div>
          </div>
        ) : (
          <div className="bg-danger-50 rounded-md p-4">
            <Text className="text-danger-500 flex justify-center" as="div">
              {t('interviewActions.choosePrompt')}
            </Text>
          </div>
        )}
      </HubertDialog.Content>
    </HubertDialog>
  );
};

export default SelectedInterviewActionsDialog;
